import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ArchiveIcon from '@panel/commons/assets/icons/archive.svg';
import { sendTrackingInfo } from '@panel/commons';
import { InboxOperations } from '@panel/leads/src/components/menuInbox/ducks';

import {
  ContainerLayout,
  Header,
  FiltersAppliedSection,
  FilterPill,
  PillsContainer,
  ClearFilterButton,
  ButtonWrapper,
  StyledButton
} from './styles';
import { FilterSelectors, FilterOperations } from '../../../ducks/filter/index';
import { PostingsSelectors } from '../../../ducks/posting';
import FacetModule from './FacetModule';
import trash from '../../../assets/icons/trash.svg';
import trashCM from '../../../assets/icons/trashCM.svg';
import trashVA from '../../../assets/icons/trashVA.svg';
import trashUB from '../../../assets/icons/trashUB.svg';
import deletePill from '../../../assets/icons/eliminar-pill.svg';
import deletePillVA from '../../../assets/icons/eliminar-pill-va.svg';
import deletePillCM from '../../../assets/icons/eliminar-pill-cm.svg';
import deletePillUB from '../../../assets/icons/eliminar-pill-ub.svg';
import Searchbox from '../commons/Searchbox';
import useUrlSearchParams from '../hooks/useUrlSearchParams';

const siteId = process.env.REACT_APP_BUILD_SITE;

const Filtro = ({ onClose = () => {}, isDesktop = false, isTablet }) => {
  const { t } = useTranslation();
  const [searchCode, setSearchCode] = useState('');
  const dispatch = useDispatch();
  const { facets, postingCount } = useSelector(PostingsSelectors.getPostingsState);
  const { appliedFilters, searchCriteria } = useSelector(FilterSelectors.getFiltersState);
  const facetsWithCompositeKey = ['realEstateType', 'province', 'city', 'zone', 'publicationPlanId'];
  const translationPath = {
    status: 'panel.filters.status.',
    publicationPlan: 'panel.avisos.planName.',
    operationType: 'panel.filters.operationType.',
    hasDuplicates: 'panel.avisos.filters.pill.',
    hasReports: 'panel.avisos.filters.pill.',
    hasQuality: 'panel.avisos.filters.pill.',
    hasReportsTotal: 'panel.avisos.filters.pill.'
  };
  const {
    formatedUrlFilters,
    deleteUrlFilter,
    cleanUrlFilters,
    setUrlSearch,
    chargeAppliedFilters,
    filterUpdateFormat
  } = useUrlSearchParams();

  useEffect(() => {
    if (formatedUrlFilters.length > 0) {
      chargeAppliedFilters();
      dispatch(InboxOperations.fetchUnread('nondiscarded', false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formatedUrlFilters]);

  useEffect(() => {
    if (!appliedFilters.searchCode.id) setSearchCode('');
  }, [appliedFilters]);

  const needTranslation = Object.keys(translationPath);

  const handleDeleteFilter = filterName => {
    deleteUrlFilter(filterName);
    dispatch(InboxOperations.fetchUnread('nondiscarded', false));
    dispatch(FilterOperations.update({ [filterName]: { id: '', displayName: '' } }));
  };

  const translate = (key, facetName) =>
    needTranslation.includes(facetName) ? t(translationPath[facetName] + key) : key;

  const handleClearFilters = () => {
    cleanUrlFilters();
    dispatch(FilterOperations.reset());
    onClose();
  };

  const handleSubmit = e => {
    setUrlSearch(e.target[0].value, 'searchCode');
    sendTrackingInfo('Panel', 'Mis avisos', 'Buscador de avisos-Buscar');
    dispatch(InboxOperations.fetchUnread('nondiscarded', false));
    dispatch(FilterOperations.update({ searchCode: { id: e.target[0].value, displayName: e.target[0].value } }));
  };

  const handleChange = e => {
    // SE EVITA LA BÚSQUEDA CON CARACTER ";" Y ","
    setSearchCode(e.target.value.replaceAll(/[;,]/g, ''));
  };

  const handleArchiveButton = () => {
    setUrlSearch('DELETED', 'status');
    sendTrackingInfo('Panel', 'Mis avisos', 'Filtros-Bandeja de archivados');
    dispatch(FilterOperations.update({ status: { id: 'DELETED', displayName: 'DELETED' } }));
  };

  // SE COMPRUEBA QUE LOS ÚNICOS FILTROS APLICADOS NO SEAN EL DE 'SORT' NI EL DE 'ONLINEFIRST', YA QUE ESTOS NO SE MUESTRAN EN LAS PILLS
  const onlySortApplied =
    (searchCriteria && searchCriteria.split(':').length === 2 && searchCriteria.split(':')[0] === 'sort') ||
    (searchCriteria &&
      searchCriteria.split(';').length === 2 &&
      searchCriteria.split(';')[0].split(':')[0] === 'sort' &&
      searchCriteria.split(';')[1].split(':')[0] === 'onlineFirst');

  const showPillsArea = searchCriteria && !onlySortApplied;

  const getDeletePill = () => {
    if (siteId === 'CMBR') return deletePillCM;
    if (siteId === 'UBPE') return deletePillUB;
    if (siteId === 'VAMX') return deletePillVA;
    return deletePill;
  };

  const renderPills = () => {
    if (!appliedFilters) return <></>;
    const keys = Object.keys(appliedFilters);
    return (
      <PillsContainer data-panel='filter-pillsContainer' isDesktop={isDesktop}>
        {keys.map(
          value =>
            value !== 'sort' &&
            value !== 'onlineFirst' &&
            appliedFilters[value].id && (
              <FilterPill key={value} data-panel='filter-pill'>
                <p data-panel='filter-pill-text'>{translate(appliedFilters[value].displayName, value)}</p>
                <img
                  data-panel='filter-pill-deleteButton'
                  src={getDeletePill()}
                  alt='Quitar filtro'
                  onClick={() => handleDeleteFilter(value)}
                  aria-hidden='true'
                />
              </FilterPill>
            )
        )}
      </PillsContainer>
    );
  };

  const getTrashIcon = () => {
    if (siteId === 'CMBR') return trashCM;
    if (siteId === 'UBPE') return trashUB;
    if (siteId === 'VAMX') return trashVA;
    return trash;
  };

  return (
    <ContainerLayout data-panel='filter-container' isDesktop={isDesktop}>
      <Header isDesktop={isDesktop} data-panel='filter-header'>
        <p data-panel='filter-header-title'>Filtros</p>
        {!isDesktop && (
          <div data-panel='filter-closeButton-mobile' onClick={onClose} aria-hidden='true'>
            <span />
          </div>
        )}
      </Header>
      {!isDesktop && !isTablet && (
        <div className='search-container'>
          <Searchbox
            data-panel='filter-searchBar-mobile'
            handleSubmit={handleSubmit}
            handleChange={handleChange}
            value={searchCode}
            placeholder={t('panel.avisos.filters.searchPlaceholder')}
          />
        </div>
      )}
      {showPillsArea && (
        <FiltersAppliedSection isDesktop={isDesktop}>
          {renderPills()}
          <ClearFilterButton data-panel='filter-clearButton' onClick={handleClearFilters} isDesktop={isDesktop}>
            {t('panel.avisos.filters.cleanFilters')}
            {!isDesktop && <img src={getTrashIcon()} alt='Limpiar filtros' />}
          </ClearFilterButton>
        </FiltersAppliedSection>
      )}
      {facets &&
        // eslint-disable-next-line array-callback-return
        facets.map(facet => {
          if (!facet.values) return;
          if (Object.keys(facet.values).length > 0) {
            // eslint-disable-next-line consistent-return
            return (
              <FacetModule
                data-panel={`filter-facetModule-${facet.facetName}`}
                isDesktop={isDesktop}
                translate={translate}
                key={facet.facetName}
                facet={facet}
                facetsWithCompositeKey={facetsWithCompositeKey}
                filterUpdateFormat={filterUpdateFormat}
                setUrlSearch={setUrlSearch}
              />
            );
          }
        })}
      {!isDesktop && (
        <ButtonWrapper>
          <StyledButton data-panel='filter-seeResults-mobile' onClick={onClose}>
            Mostrar resultado {searchCriteria ? `(${postingCount})` : ''}
          </StyledButton>
        </ButtonWrapper>
      )}
      <button className='btn-archive' data-panel='filter-archiveButton' onClick={handleArchiveButton} type='button'>
        <img src={ArchiveIcon} alt='Archivados' />
        <p data-panel='filter-archiveButton-text'>{t('panel.avisos.filters.archived')}</p>
      </button>
    </ContainerLayout>
  );
};

export default Filtro;
